/**
 * What a prescription upload accepts (ORD-06, ORD-15). The API publishes it
 * (GET /catalogue/upload-rules) from the same check the upload applies; RX_UPLOAD_RULE is the
 * server's default, used until the published rule has loaded. No HEIC (D-12): with HEIC left out of
 * the file input's `accept`, iPhone Safari hands over a JPEG instead.
 */

export interface UploadRule {
  max_bytes: number;
  types: string[];
  extensions: string[];
}

export const RX_UPLOAD_RULE: UploadRule = {
  max_bytes: 10 * 1024 * 1024,
  types: ["image/jpeg", "image/png", "application/pdf"],
  extensions: ["jpg", "png", "pdf", "jpeg"],
};

/** A support message's attachments (SUP-10: the picker allowed 3 MB and any number of files). */
export const SUPPORT_UPLOAD_RULE: UploadRule = {
  max_bytes: 10 * 1024 * 1024,
  types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
  extensions: ["jpg", "png", "webp", "pdf", "jpeg"],
};
/** How many files one support message may carry (SUPPORT_ATTACHMENTS_PER_MESSAGE). */
export const SUPPORT_FILES_PER_MESSAGE = 5;

/** The file input's `accept`: the types, plus the extensions for a browser that reports no type. */
export const acceptFor = (rule: UploadRule): string =>
  [...rule.types, ...rule.extensions.map((e) => `.${e}`)].join(",");

/** "JPG, PNG or PDF" — the formats as people know them. */
export function formatNames(rule: UploadRule): string {
  const names = rule.extensions.filter((e) => e !== "jpeg" || !rule.extensions.includes("jpg")).map((e) => e.toUpperCase());
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}` : names.join("");
}

export const megabytes = (bytes: number) => `${Number((bytes / (1024 * 1024)).toFixed(1))} MB`;

export const typeRefused = (rule: UploadRule) =>
  `That file type can't be uploaded. Upload a ${formatNames(rule)} file.`;

export const tooLarge = (rule: UploadRule) =>
  `That file is larger than ${megabytes(rule.max_bytes)}. Choose a smaller file or a clearer, smaller photo.`;

/** Why this file can't be uploaded, or null when it can. With no type, the file name decides. */
export function uploadProblem(file: { type: string; name: string; size: number }, rule: UploadRule): string | null {
  if (file.size > rule.max_bytes) return tooLarge(rule);
  const type = file.type.toLowerCase();
  const ext = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  const known = type !== "" && type !== "application/octet-stream";
  const ok = known ? rule.types.includes(type === "image/jpg" ? "image/jpeg" : type) : ext === "" || rule.extensions.includes(ext);
  return ok ? null : typeRefused(rule);
}
